import './chart-setup';

import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { forkJoin, interval, Subscription } from 'rxjs';
import * as L from 'leaflet';
import { feature as topoFeature } from 'topojson-client';
import * as countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
countriesLib.registerLocale(enLocale);

import {
  BreakdownItem,
  DateRange,
  PlausibleAggregate,
  PlausibleService,
  TimeseriesPoint,
} from '../shared/services/plausible.service';
import { TreeNode } from 'primeng/api';

const PALETTE = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16',
];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
  standalone: false,
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  protected readonly Math = Math;

  // ── État ─────────────────────────────────────────────────────────────────
  loading = true;
  isLive  = false;

  // ── Données ───────────────────────────────────────────────────────────────
  aggregate:  PlausibleAggregate | null = null;
  realtime    = 0;
  timeseries: TimeseriesPoint[]  = [];
  pages:      BreakdownItem[]    = [];
  entryPages: BreakdownItem[]    = [];
  exitPages:  BreakdownItem[]    = [];
  countries:  BreakdownItem[]    = [];
  devices:    BreakdownItem[]    = [];
  sources:    BreakdownItem[]    = [];
  pageTree:   TreeNode[]         = [];

  // ── Filtre de date ────────────────────────────────────────────────────────
  readonly presets = [
    { label: "Aujourd'hui", value: 'day'   },
    { label: '7 jours',     value: '7d'    },
    { label: '30 jours',    value: '30d'   },
    { label: 'Ce mois',     value: 'month' },
    { label: '12 mois',     value: '12mo'  },
    { label: 'Tout',        value: 'all'   },
    { label: 'Personnalisé',value: 'custom'},
  ];
  selectedPreset  = '7d';
  currentRange: DateRange = ''; // initialisé dans ngOnInit via presetToRange
  showDatePicker  = false;
  today           = new Date();
  customDates: Date[] | null = null;

  // ── Chart.js data ─────────────────────────────────────────────────────────
  timeseriesData:  Record<string, unknown> = { labels: [], datasets: [] };
  timeseriesOptions = this.buildLineOptions();

  devicesData:     Record<string, unknown> = { labels: [], datasets: [] };
  sourcesData:     Record<string, unknown> = { labels: [], datasets: [] };
  entryExitData:   Record<string, unknown> = { labels: [], datasets: [] };
  donutOptions     = this.buildDonutOptions();
  entryExitOptions = this.buildEntryExitOptions();

  // ── Leaflet map ───────────────────────────────────────────────────────────
  @ViewChild('geoMapDiv') geoMapDivRef?: ElementRef<HTMLDivElement>;
  private leafletMap?: L.Map;
  private geoLayer?: L.GeoJSON;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private worldFeatures?: any[];

  // ── Polling ───────────────────────────────────────────────────────────────
  private liveSubs: Subscription[] = [];

  constructor(private plausible: PlausibleService) {}

  ngOnInit(): void {
    this.currentRange = this.presetToRange(this.selectedPreset);
    this.loadAll();
    this.startPolling();
  }

  ngAfterViewInit(): void {
    this.initLeafletMap();
    this.loadWorldAtlas();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.leafletMap?.remove();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Sélection de période
  // ══════════════════════════════════════════════════════════════════════════

  setPeriod(value: string): void {
    if (value === 'custom') {
      this.selectedPreset = 'custom';
      this.showDatePicker = true;
      return;
    }
    this.selectedPreset = value;
    this.showDatePicker = false;
    this.currentRange   = this.presetToRange(value);
    this.loadAll();
    this.startPolling();
  }

  onCustomDatesSelect(): void {
    if (this.customDates?.[0] && this.customDates?.[1]) {
      const from = this.toISODate(this.customDates[0]);
      const to   = this.toISODate(this.customDates[1]);
      this.currentRange = `${from},${to}`;
      this.loadAll();
      this.startPolling();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Chargement
  // ══════════════════════════════════════════════════════════════════════════

  loadAll(showSpinner = true): void {
    if (showSpinner) this.loading = true;
    const r  = this.currentRange;
    const iv = this.timeseriesInterval;

    this.plausible.getStats(r).subscribe({
      next:  agg => { this.aggregate = agg; this.loading = false; },
      error: ()  => (this.loading = false),
    });

    this.plausible.getTimeseries(r, iv).subscribe({
      next: pts => { this.timeseries = pts; this.buildTimeseriesChart(pts); },
    });

    this.plausible.getPages(r).subscribe({
      next: items => { this.pages = items; this.pageTree = this.buildPageTree(items); },
    });

    forkJoin({
      entry: this.plausible.getEntryPages(r),
      exit:  this.plausible.getExitPages(r),
    }).subscribe(({ entry, exit }) => {
      this.entryPages = entry;
      this.exitPages  = exit;
      this.buildEntryExitChart(entry, exit);
    });

    this.plausible.getCountries(r).subscribe({
      next: items => {
        this.countries = items;
        if (this.worldFeatures) this.renderLeafletMap(items);
      },
    });

    this.plausible.getDevices(r).subscribe({
      next: items => { this.devices = items; this.buildDevicesChart(items); },
    });

    this.plausible.getSources(r).subscribe({
      next: items => { this.sources = items; this.buildSourcesChart(items); },
    });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Polling live
  // ══════════════════════════════════════════════════════════════════════════

  private startPolling(): void {
    this.stopPolling();
    if (!this.rangeIncludesToday(this.currentRange)) { this.isLive = false; return; }
    this.isLive = true;

    this.liveSubs.push(
      interval(15_000).subscribe(() =>
        this.plausible.getRealtime().subscribe(n => (this.realtime = n))),
    );
    this.liveSubs.push(
      interval(60_000).subscribe(() => this.loadAll(false)),
    );
  }

  private stopPolling(): void {
    this.liveSubs.forEach(s => s.unsubscribe());
    this.liveSubs = [];
    this.isLive   = false;
  }

  private rangeIncludesToday(range: DateRange): boolean {
    if (!range.includes(',')) return true;
    const [, end] = range.split(',');
    return end >= this.toISODate(new Date());
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Getters / Utilitaires
  // ══════════════════════════════════════════════════════════════════════════

  get timeseriesInterval(): 'day' | 'hour' {
    if (this.currentRange === 'day') return 'hour';
    if (this.currentRange.includes(',')) {
      const [from, to] = this.currentRange.split(',');
      const days = (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000;
      return days <= 2 ? 'hour' : 'day';
    }
    return 'day';
  }

  get maxCountryVisitors(): number {
    return this.countries[0]?.visitors ?? 1;
  }

  toFlagEmoji(code: string): string {
    if (!code || code.length !== 2) return '🌍';
    return [...code.toUpperCase()]
      .map(c => String.fromCodePoint(c.charCodeAt(0) + 127_397))
      .join('');
  }

  countryName(alpha2: string): string {
    return countriesLib.getName(alpha2, 'en') ?? alpha2;
  }

  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  formatNumber(n: number): string {
    return new Intl.NumberFormat('fr-FR').format(n ?? 0);
  }

  sourceIcon(source: string): string {
    const s = (source ?? '').toLowerCase();
    if (!s || s === 'direct / none') return '🔗';
    if (s.includes('google'))                         return '🔍';
    if (s.includes('twitter') || s.includes('x.com')) return '𝕏';
    if (s.includes('facebook') || s.includes('fb'))   return '👥';
    if (s.includes('linkedin'))                        return '💼';
    if (s.includes('github'))                          return '🐙';
    if (s.includes('youtube'))                         return '▶️';
    return '🌐';
  }

  deviceIcon(device: string): string {
    switch ((device ?? '').toLowerCase()) {
      case 'desktop': return '🖥️';
      case 'mobile':  return '📱';
      case 'tablet':  return '⬛';
      default:        return '💻';
    }
  }

  private toISODate(d: Date): string {
    // Utiliser les méthodes locales pour éviter le décalage UTC (toISOString passe en UTC)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Convertit un preset en date_range envoyé à Plausible.
   *  Pour 7d/30d on calcule une plage explicite incluant aujourd'hui
   *  car le preset v2 "7d" exclut today et diffère du dashboard Plausible. */
  private presetToRange(preset: string): DateRange {
    const today = new Date();
    const todayStr = this.toISODate(today);
    switch (preset) {
      case '7d': {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        return `${this.toISODate(from)},${todayStr}`;
      }
      case '30d': {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return `${this.toISODate(from)},${todayStr}`;
      }
      default:
        return preset; // day, month, 12mo, all → preset Plausible natif
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Arbre des pages (toujours ouvert)
  // ══════════════════════════════════════════════════════════════════════════

  buildPageTree(pages: BreakdownItem[]): TreeNode[] {
    type NodeEntry = TreeNode & { _v: number; _pv: number };
    const map = new Map<string, NodeEntry>();

    for (const page of pages) {
      const parts  = page.label.split('/').filter(Boolean);
      const topKey = '/' + (parts[0] ?? '');

      if (!map.has(topKey)) {
        map.set(topKey, {
          label: topKey, data: { visitors: 0, pageviews: 0 },
          children: [], expanded: true, _v: 0, _pv: 0,
        });
      }

      const node = map.get(topKey)!;
      node._v  += page.visitors;
      node._pv += page.pageviews ?? 0;
      node.data  = { visitors: node._v, pageviews: node._pv };

      if (parts.length > 1) {
        node.children!.push({
          label: page.label,
          data:  { visitors: page.visitors, pageviews: page.pageviews ?? 0 },
          leaf:  true,
        });
      }
    }

    return [...map.values()]
      .sort((a, b) => b._v - a._v)
      .map(({ _v, _pv, ...rest }) => {
        void _v; void _pv;
        if ((rest.children?.length ?? 0) === 0) rest.leaf = true;
        return rest as TreeNode;
      });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Données Chart.js
  // ══════════════════════════════════════════════════════════════════════════

  private buildTimeseriesChart(points: TimeseriesPoint[]): void {
    const sparse = points.length > 60;
    this.timeseriesData = {
      labels: points.map(p => p.date),
      datasets: [
        {
          label: 'Visiteurs',
          data:  points.map(p => p.visitors),
          fill: true, borderColor: PALETTE[0], backgroundColor: PALETTE[0] + '22',
          tension: 0.4, pointRadius: sparse ? 0 : 3,
        },
        {
          label: 'Pages vues',
          data:  points.map(p => p.pageviews),
          fill: true, borderColor: PALETTE[1], backgroundColor: PALETTE[1] + '22',
          tension: 0.4, pointRadius: sparse ? 0 : 3,
        },
      ],
    };
  }

  private buildDevicesChart(items: BreakdownItem[]): void {
    this.devicesData = {
      labels:   items.map(i => i.label),
      datasets: [{ data: items.map(i => i.visitors), backgroundColor: PALETTE, borderWidth: 0, hoverOffset: 8 }],
    };
  }

  private buildSourcesChart(items: BreakdownItem[]): void {
    this.sourcesData = {
      labels:   items.map(i => i.label || '(Direct)'),
      datasets: [{ data: items.map(i => i.visitors), backgroundColor: PALETTE, borderWidth: 0, hoverOffset: 8 }],
    };
  }

  private buildEntryExitChart(entry: BreakdownItem[], exit: BreakdownItem[]): void {
    const map = new Map<string, { entry: number; exit: number }>();
    for (const e of entry) map.set(e.label, { entry: e.visitors, exit: 0 });
    for (const x of exit) {
      const existing = map.get(x.label);
      if (existing) existing.exit = x.visitors;
      else map.set(x.label, { entry: 0, exit: x.visitors });
    }

    const sorted = [...map.entries()]
      .sort((a, b) => (b[1].entry + b[1].exit) - (a[1].entry + a[1].exit))
      .slice(0, 10);

    this.entryExitData = {
      labels: sorted.map(([label]) => label),
      datasets: [
        {
          label: 'Entrées',
          data:  sorted.map(([, v]) => v.entry),
          backgroundColor: '#10b98133', borderColor: '#10b981', borderWidth: 2, borderRadius: 4,
        },
        {
          label: 'Sorties',
          data:  sorted.map(([, v]) => v.exit),
          backgroundColor: '#ef444433', borderColor: '#ef4444', borderWidth: 2, borderRadius: 4,
        },
      ],
    };
  }

  private buildLineOptions(): Record<string, unknown> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend:  { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: { padding: 12, cornerRadius: 8 },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxTicksLimit: 10 } },
        y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
      },
    };
  }

  private buildDonutOptions(): Record<string, unknown> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend:  { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 12 } },
        tooltip: { padding: 10, cornerRadius: 8 },
      },
    };
  }

  private buildEntryExitOptions(): Record<string, unknown> {
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend:  { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: { padding: 10, cornerRadius: 8 },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: '#f3f4f6' },
          ticks: {
            stepSize: 1,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            callback: (value: any) => Number.isInteger(value) ? value : '',
          },
        },
        y: { grid: { display: false }, ticks: { font: { family: 'monospace', size: 11 } } },
      },
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Carte Leaflet (zoom + pan)
  // ══════════════════════════════════════════════════════════════════════════

  private initLeafletMap(): void {
    if (!this.geoMapDivRef) return;

    this.leafletMap = L.map(this.geoMapDivRef.nativeElement, {
      center: [52, 12],
      zoom: 3.5,
      minZoom: 1.5,
      maxZoom: 8,
      zoomSnap: 0.5,
      scrollWheelZoom: true,
      preferCanvas: true,
    });

    // Fond CartoDB Positron (sans labels) — gratuit, aucune clé requise
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
      maxZoom: 19,
      attribution: '© <a href="https://carto.com/">CARTO</a>',
    }).addTo(this.leafletMap);
  }

  private loadWorldAtlas(): void {
    fetch('/assets/geo/countries-110m.json')
      .then(r => r.json())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((world: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const geoJson: any = topoFeature(world, world.objects.countries);
        this.worldFeatures = geoJson.features;
        if (this.countries.length > 0) this.renderLeafletMap(this.countries);
      })
      .catch(() => console.warn('Carte géo non disponible'));
  }

  private renderLeafletMap(data: BreakdownItem[]): void {
    if (!this.leafletMap || !this.worldFeatures) return;

    const visitorsMap = new Map<string, number>(
      data.map(d => [d.label.toUpperCase(), d.visitors]),
    );
    const maxVisitors = data[0]?.visitors ?? 1;

    // Supprimer le layer précédent
    this.geoLayer?.remove();

    this.geoLayer = L.geoJSON(
      { type: 'FeatureCollection', features: this.worldFeatures } as GeoJSON.GeoJsonObject,
      {
        style: (feature) => {
          const numericId = (feature?.id as number | string)?.toString().padStart(3, '0');
          const alpha2    = countriesLib.numericToAlpha2(numericId)?.toUpperCase() ?? '';
          const visitors  = visitorsMap.get(alpha2) ?? 0;
          const intensity = visitors > 0 ? 0.15 + (visitors / maxVisitors) * 0.75 : 0.03;

          return {
            fillColor:   '#6366f1',
            fillOpacity: visitors > 0 ? 0.15 + (visitors / maxVisitors) * 0.7 : 0,
            color:       '#ffffff',
            weight:      0.5,
            opacity:     0.4,
          };
        },
        onEachFeature: (feature, layer) => {
          const numericId = (feature?.id as number | string)?.toString().padStart(3, '0');
          const alpha2    = countriesLib.numericToAlpha2(numericId) ?? '';
          const name      = countriesLib.getName(alpha2, 'en') ?? alpha2;
          const visitors  = visitorsMap.get(alpha2.toUpperCase()) ?? 0;
          const flag      = this.toFlagEmoji(alpha2);

          layer.bindTooltip(
            `<div class="text-xs font-medium">${flag} ${name}</div>
             <div class="text-xs text-gray-600">${this.formatNumber(visitors)} visiteur${visitors > 1 ? 's' : ''}</div>`,
            { sticky: true, className: 'leaflet-analytics-tooltip' },
          );
        },
      },
    ).addTo(this.leafletMap);

    // Ne pas recentrer — on garde la vue Europe définie à l'init
  }
}
